import { put, takeLatest, call, take } from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import request from '@iso/lib/helpers/httpClient';
import { initChatClient, disconnectChatClient } from '@iso/lib/chatClient';
import * as ACTIONS from './actions';
import * as AUTH_CONSTANTS from '../auth/constants';
import { SIGNOUT } from '../auth/constants';

function createChatEventChannel(client) {
  return eventChannel((emit) => {
    const handleEvent = (event) => {
      // Emit the total unread count from the user object
      if (client.user) {
        emit(client.user.unread_channels);
      }
    };

    // Listen for relevant events
    client.on('notification.message_new', handleEvent);
    client.on('notification.mark_read', handleEvent);
    client.on('message.new', handleEvent);
    client.on('message.read', handleEvent);
    client.on('channel.updated', handleEvent);

    return () => {
      client.off('notification.message_new', handleEvent);
      client.off('notification.mark_read', handleEvent);
      client.off('message.new', handleEvent);
      client.off('message.read', handleEvent);
      client.off('channel.updated', handleEvent);
    };
  });
}

function* watchChatEvents(client) {
  const channel = yield call(createChatEventChannel, client);
  try {
    while (true) {
      const count = yield take(channel);
      yield put(ACTIONS.setUnreadMessageCount(count));
    }
  } finally {
    channel.close();
  }
}

function* initChatOnAuth(action) {
  // action may be START_WITH_TOKEN_SUCCESS (action.data)
  // or SIGNIN_SUCCESS/GOOGLE_SIGNIN_SUCCESS (action.payload)
  const user = (action.data && action.data.user) ||
    (action.payload && action.payload.user) ||
    action.data ||
    action.payload;

  if (!user) return;

  try {
    // Fetch the stream token from the API endpoint
    const tokenData = yield call(request, '/stream/token/', 'GET', null, true);
    const userToken = tokenData.token || null;
    const userChatId = tokenData.user_id || null;
    
    if (!userToken) {
      console.warn('No Stream token returned from API; chat not initialized');
      return;
    }

    yield put(ACTIONS.setChatId(userChatId));
    
    // Initialize the client
    const client = yield call(initChatClient, user, userToken, userChatId);

    // Set initial unread count
    if (client.user) {
      yield put(ACTIONS.setUnreadMessageCount(client.user.unread_channels));
    }

    // Start watching for events
    // Note: This blocks, so it should be forked if we had other sequential logic, 
    // but here it's fine as the last step of this saga or we can fork it.
    // However, since takeLatest cancels previous running tasks, we need to be careful.
    // initChatOnAuth is triggered once per session. 
    yield call(watchChatEvents, client);

  } catch (err) {
    console.error('Error fetching stream token or initializing chat client', err);
    yield put(ACTIONS.messagesError(err));
  }
}

function* cleanupChatOnSignout() {
  try {
    yield call(disconnectChatClient);
    yield put(ACTIONS.setChatId(null));
    yield put(ACTIONS.setUnreadMessageCount(0));
  } catch (err) {
    console.warn('Error disconnecting chat client on signout', err);
  }
}

export default function* messagesSaga() {
  yield takeLatest([
    AUTH_CONSTANTS.SIGNIN_SUCCESS,
    AUTH_CONSTANTS.GOOGLE_SIGNIN_SUCCESS,
    AUTH_CONSTANTS.START_WITH_TOKEN_SUCCESS
  ], initChatOnAuth);
  
  yield takeLatest(SIGNOUT, cleanupChatOnSignout);
}
